import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Skeleton, SkeletonRows } from "./Skeleton";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

export interface Column<T> {
  /** Stable key for React; matches a data key by default but you can pass any string. */
  key: string;
  header: ReactNode;
  /** How wide the column is. Numbers are px; strings are passed through. */
  width?: number | string;
  /** Cell renderer. */
  render: (row: T) => ReactNode;
  /** Right-align (numeric / currency). */
  numeric?: boolean;
  /** Center-align. */
  center?: boolean;
  /** Sortable key — when present, header becomes clickable + emits sort events. */
  sortKey?: string;
  /** sr-only header alternative when icon-only. */
  ariaLabel?: string;
  /** Sticky right (action column). */
  stickyRight?: boolean;
}

interface Props<T> {
  data: T[] | undefined;
  columns: Column<T>[];
  /** Stable row key — uses `id` by default if present. */
  rowKey?: (row: T) => string;
  /** Click handler for a row. Renders the row with hover affordance. */
  onRowClick?: (row: T) => void;
  loading?: boolean;
  error?: unknown;
  empty?: { title: string; description?: ReactNode; actions?: ReactNode };
  /** Server-side sort state + setter. Omit for unsorted tables. */
  sort?: { key: string; direction: "asc" | "desc" } | null;
  onSortChange?: (key: string, direction: "asc" | "desc") => void;
  /** Aria-label for the table (required for a11y). */
  ariaLabel: string;
  /** Density — default "comfortable". "compact" for list-heavy operational screens. */
  density?: "compact" | "comfortable";
  /** Slot below the table — usually <Pagination />. */
  footer?: ReactNode;
  /**
   * Multi-select. When `selectedIds` is provided, a leading checkbox column is rendered
   * (header = select-all of the current page). `onSelectionChange` receives the next full set.
   */
  selectedIds?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
}

/** Reusable table with sticky header, sortable columns, loading/empty/error states. */
export function DataTable<T>({
  data,
  columns,
  rowKey,
  onRowClick,
  loading,
  error,
  empty,
  sort,
  onSortChange,
  ariaLabel,
  density = "comfortable",
  footer,
  selectedIds,
  onSelectionChange,
}: Props<T>) {
  const selectable = !!selectedIds && !!onSelectionChange;
  // Local fallback sort if no server-side handler provided
  const [localSort, setLocalSort] = useState<typeof sort>(null);
  const activeSort = sort !== undefined ? sort : localSort;

  function toggleSort(col: Column<T>) {
    if (!col.sortKey) return;
    const cur = activeSort;
    const next: NonNullable<typeof activeSort> =
      cur?.key === col.sortKey
        ? { key: col.sortKey, direction: cur.direction === "asc" ? "desc" : "asc" }
        : { key: col.sortKey, direction: "asc" };
    if (onSortChange) onSortChange(next.key, next.direction);
    else setLocalSort(next);
  }

  const computedRowKey = useMemo(() => {
    if (rowKey) return rowKey;
    return (row: T) => {
      const r = row as unknown as { id?: string | number };
      return String(r.id ?? Math.random());
    };
  }, [rowKey]);

  const hasRows = !loading && !error && data && data.length > 0;

  const allIds = hasRows ? data!.map(computedRowKey) : [];
  const allSelected =
    selectable && allIds.length > 0 && allIds.every((id) => selectedIds!.has(id));
  const someSelected =
    selectable && allIds.some((id) => selectedIds!.has(id)) && !allSelected;
  function toggleAll() {
    const next = new Set(selectedIds);
    if (allSelected) allIds.forEach((id) => next.delete(id));
    else allIds.forEach((id) => next.add(id));
    onSelectionChange!(next);
  }
  function toggleRow(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange!(next);
  }

  const totalCols = columns.length + (selectable ? 1 : 0);
  const cellPad = density === "compact" ? "py-1.5" : "py-2.5";

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table aria-label={ariaLabel} aria-rowcount={data?.length ?? 0}>
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-transparent">
              {selectable && (
                <TableHead className="w-9 text-center">
                  <Checkbox
                    aria-label="Chọn tất cả"
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
              )}
              {columns.map((col) => {
                // Guard both sides: with no sort state, `activeSort?.key`
                // and a non-sortable `col.sortKey` are both `undefined`,
                // which would wrongly compare equal and then deref null.
                const isSorted =
                  !!activeSort && !!col.sortKey && activeSort.key === col.sortKey;
                return (
                  <TableHead
                    key={col.key}
                    style={{
                      width: typeof col.width === "number" ? `${col.width}px` : col.width,
                    }}
                    className={cn(
                      col.numeric ? "text-right" : col.center ? "text-center" : "text-left",
                      col.stickyRight && "sticky right-0 bg-muted/50"
                    )}
                    aria-sort={
                      isSorted
                        ? activeSort!.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : col.sortKey
                          ? "none"
                          : undefined
                    }
                  >
                    {col.sortKey ? (
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground",
                          col.numeric && "flex-row-reverse",
                          col.center && "mx-auto"
                        )}
                        onClick={() => toggleSort(col)}
                        aria-label={col.ariaLabel}
                      >
                        {col.header}
                        <span aria-hidden="true">
                          {isSorted ? (
                            activeSort!.direction === "asc" ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowDown className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                          )}
                        </span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={totalCols} className="p-0">
                  <SkeletonRows
                    rows={8}
                    cols={columns.map((c) =>
                      typeof c.width === "number" ? c.width : "1fr"
                    )}
                  />
                </TableCell>
              </TableRow>
            )}
            {!!error && !loading && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={totalCols} className="p-4">
                  <ErrorState error={error} inline />
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && data && data.length === 0 && empty && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={totalCols}>
                  <EmptyState
                    title={empty.title}
                    description={empty.description}
                    actions={empty.actions}
                  />
                </TableCell>
              </TableRow>
            )}
            {hasRows &&
              data!.map((row) => (
                <TableRow
                  key={computedRowKey(row)}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter") onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {selectable &&
                    (() => {
                      const id = computedRowKey(row);
                      return (
                        <TableCell
                          className={cn("text-center", cellPad)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            aria-label="Chọn dòng"
                            checked={selectedIds!.has(id)}
                            onCheckedChange={() => toggleRow(id)}
                          />
                        </TableCell>
                      );
                    })()}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        cellPad,
                        col.numeric ? "text-right" : col.center ? "text-center" : undefined,
                        col.stickyRight && "sticky right-0 bg-background"
                      )}
                    >
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      {footer}
    </div>
  );
}

/** Convenience: cell that shows a single line of skeleton while parent loads. */
export function CellSkeleton() {
  return <Skeleton width="80%" height={12} />;
}
